import axios from 'axios';

const REVENUE_API = '/api/v2/tables/revenue';

/** Колонка отчета аптеки (строка table_info) со справочными данными, которые считает сервер */
export interface RevenueColumn {
  id: number;
  cName: string;
  cType: string;
  displayName: string;
  /** 1 - ввод заблокирован (поле заполняется системой или по формуле) */
  InputBlock: number;
  formula: string;
  sortByGroup: string;
  important: number;
  statisticsHide: number;
  hideInStatTable: number;
  /** В скольких таблицах аптек есть колонка; null, если сервер не смог это определить или нет права sync.view */
  inTables: number | null;
  /** Служебная колонка, на которой держатся отчеты: ее нельзя удалить */
  isProtected: boolean;
  /** Колонку заполняет система: блокировку ввода и формулу менять нельзя */
  isSystemFilled: boolean;
  /** Отображаемые имена колонок, в формулах которых используется эта */
  usedIn: string[];
}

export interface RevenueList {
  columns: RevenueColumn[];
  /** Сколько всего таблиц аптек; null, если сервер не смог это определить или нет права sync.view */
  pharmacyTables: number | null;
}

/** Значения формы колонки в том виде, в каком их ждет сервер */
export interface RevenueDraft {
  cName: string;
  cType: string;
  displayName: string;
  sortByGroup: string;
  InputBlock: number;
  formula: string;
  important: number;
  statisticsHide: number;
  hideInStatTable: number;
}

// ---------------------------------------------------------------------------
// Длительные операции над таблицами аптек
// ---------------------------------------------------------------------------

export type JobStatus = 'running' | 'done' | 'incomplete';
export type OperationKind = 'add_column' | 'sync_column' | 'recalc';

/** Таблица аптеки, в которую колонка не добавилась: busy - занята запросами, rebuild - нужна перестройка */
export interface SyncFailure {
  table: string;
  kind: 'busy' | 'rebuild' | 'error' | 'aborted' | string;
  message?: string;
}

export interface RecalcSample {
  table: string;
  date: string;
  field: string;
  label: string;
  from: number;
  to: number;
}

export interface RecalcFieldCount {
  name: string;
  label: string;
  count: number;
}

export interface RecalcFailure {
  table: string;
  message: string;
}

/** Итог пересчета истории (и предпросмотра, и выполненной операции) */
export interface RecalcSummary {
  from: string;
  applied: boolean;
  aborted: boolean;
  remaining: number;
  /** Сколько таблиц аптек просмотрено */
  tables: number;
  changedTables: number;
  days: number;
  values: number;
  byField: RecalcFieldCount[];
  samples: RecalcSample[];
  skipped: number;
  failedCount: number;
  failed: RecalcFailure[];
}

/** Итог операции: у добавления колонки заполнены поля охвата, у пересчета - поля RecalcSummary */
export interface JobResult extends Partial<RecalcSummary> {
  present?: number;
  added?: number;
  failures?: SyncFailure[];
  warning?: string | null;
  id?: number;
}

export interface OperationJob {
  id: string;
  op: OperationKind;
  title: string;
  label: string;
  by: string;
  startedAt: number;
  finishedAt: number | null;
  status: JobStatus;
  progress: { done: number; total: number };
  message: string;
  result: JobResult | null;
}

/** Действующая блокировка данных аптек; время - миллисекунды Unix */
export interface DataLock {
  op: string;
  title: string;
  by: string;
  since: number;
  until: number;
}

export interface OperationState {
  lock: DataLock | null;
  job: OperationJob | null;
}

/** Охват колонки по таблицам аптек (GET /:id/tables) */
export interface ColumnCoverage {
  total: number;
  present: number;
  /** Имена таблиц без колонки (не больше нескольких десятков) */
  missing: string[];
  missingCount: number;
  /** В скольких таблицах колонка есть, но с другим типом данных */
  mismatched: number;
}

export interface CreateResult {
  /** Колонку добавляют в таблицы аптек в фоне: запись справочника появится после этой операции */
  operation: OperationJob | null;
  message: string;
  warning: string | null;
}

// ---------------------------------------------------------------------------
// Колонки
// ---------------------------------------------------------------------------

interface ListResponse {
  success?: boolean;
  data?: RevenueColumn[];
  pharmacyTables?: number | null;
}

export async function fetchRevenue(): Promise<RevenueList> {
  const res = await axios.get<ListResponse>(REVENUE_API);
  if (!res.data || !res.data.success || !Array.isArray(res.data.data)) {
    throw new Error('Сервер вернул колонки таблицы в неожиданном виде');
  }
  return {
    columns: res.data.data,
    pharmacyTables: typeof res.data.pharmacyTables === 'number' ? res.data.pharmacyTables : null
  };
}

interface CreateResponse {
  success?: boolean;
  message?: string;
  warning?: string | null;
  operation?: OperationJob | null;
}

/** Ответ 200 - колонка создана сразу; 202 - ее сначала добавляют в таблицы аптек (ход - в operation) */
export async function createColumn(draft: RevenueDraft): Promise<CreateResult> {
  const res = await axios.post<CreateResponse>(REVENUE_API, draft);
  const body = res.data || {};
  return {
    operation: body.operation || null,
    message: body.message || '',
    warning: body.warning || null
  };
}

/** Имя и тип колонки сервер не меняет: они уже заложены в таблицы аптек */
export async function updateColumn(id: number, draft: RevenueDraft): Promise<void> {
  await axios.put(`${REVENUE_API}/${id}`, draft);
}

export async function deleteColumn(id: number): Promise<void> {
  await axios.delete(`${REVENUE_API}/${id}`);
}

// ---------------------------------------------------------------------------
// Операции: блокировка, добавление колонки в таблицы аптек, пересчет истории
// ---------------------------------------------------------------------------

interface OperationResponse {
  success?: boolean;
  lock?: DataLock | null;
  job?: OperationJob | null;
}

/** Блокировка данных и ход текущей (или только что завершенной) операции */
export async function fetchOperation(): Promise<OperationState> {
  const res = await axios.get<OperationResponse>(`${REVENUE_API}/operation`);
  if (!res.data || !res.data.success) throw new Error('Сервер вернул состояние операции в неожиданном виде');
  return { lock: res.data.lock || null, job: res.data.job || null };
}

/** Снимает блокировку вручную; возвращает текст ответа сервера */
export async function releaseLock(): Promise<string> {
  const res = await axios.delete<{ message?: string }>(`${REVENUE_API}/lock`);
  return (res.data && res.data.message) || 'Блокировка снята';
}

export async function fetchColumnCoverage(id: number): Promise<ColumnCoverage> {
  const res = await axios.get<{ success?: boolean; data?: ColumnCoverage }>(`${REVENUE_API}/${id}/tables`);
  if (!res.data || !res.data.success || !res.data.data) {
    throw new Error('Сервер вернул охват колонки в неожиданном виде');
  }
  return res.data.data;
}

interface StartResponse {
  message?: string;
  operation?: OperationJob | null;
}

export interface StartResult {
  operation: OperationJob | null;
  message: string;
}

/** Добавляет существующую колонку в таблицы аптек, где ее нет. operation = null, если добавлять уже некуда */
export async function syncColumn(id: number): Promise<StartResult> {
  const res = await axios.post<StartResponse>(`${REVENUE_API}/${id}/sync`);
  const body = res.data || {};
  return { operation: body.operation || null, message: body.message || '' };
}

/** Что изменит пересчет истории с даты from (YYYY-MM-DD); ничего не записывает */
export async function previewRecalc(from: string): Promise<RecalcSummary> {
  const res = await axios.post<{ success?: boolean; data?: RecalcSummary }>(`${REVENUE_API}/recalc/preview`, { from });
  if (!res.data || !res.data.success || !res.data.data) {
    throw new Error('Сервер вернул предпросмотр пересчета в неожиданном виде');
  }
  return res.data.data;
}

/** Запускает пересчет истории в фоне под блокировкой данных */
export async function startRecalc(from: string): Promise<StartResult> {
  const res = await axios.post<StartResponse>(`${REVENUE_API}/recalc`, { from });
  const body = res.data || {};
  return { operation: body.operation || null, message: body.message || '' };
}
